import React from 'react';
import { motion } from 'framer-motion';
import faqBg from '../assets/faq.png'; 
import { Layers, UserCheck, Zap, Wallet, Search } from 'lucide-react';
import kidBg from '../assets/bgcloud.png';
import investImg from '../assets/mf-whoshouldinvest.png';
import assistBg from '../assets/mfhwa.jpg';
import banner2 from '../assets/banner2.png';
import mutualfunds from '../assets/mf1.png';
import thinkingInvestor from '../assets/thinkingman.png';
const MutualFundsHero = () => {
    const benefits = [
    {
      title: "Diversification",
      desc: "Your investment is spread across multiple securities, helping reduce the impact of market volatility.",
      icon: <Layers size={24} className="text-red-600" />
    },
    {
      title: "Professional Management",
      desc: "Experienced fund managers handle research, selection, and monitoring of the portfolio.",
      icon: <UserCheck size={24} className="text-red-600" />
    },
    {
      title: "Flexibility",
      desc: "Choose from a wide range of fund categories based on your goals and risk appetite.",
      icon: <Zap size={24} className="text-red-600" />
    },
    {
      title: "Accessibility",
      desc: "Start with smaller amounts through SIPs or lumpsum investments.",
      icon: <Wallet size={24} className="text-red-600" />
    },
    {
      title: "Transparency",
      desc: "Regular disclosures and portfolio updates help you stay informed.",
      icon: <Search size={24} className="text-red-600" />
    }
  ];
  const targetAudience = [
    "Individuals seeking long-term capital appreciation or regular income.",
    "First-time investors looking for a structured entry into financial markets.",
    "Working professionals and families planning for key life milestones.",
    "Investors who prefer a regulated, transparent, and professionally managed investment route.",
    "Those looking to diversify across asset classes through a single investment vehicle.",
    "Investors seeking goal-aligned allocation based on risk profile and time horizon."
  ];
  return (
    <div className="bg-white">
      {/* ================= HEADER BANNER ================= */}
    <section
  className="relative py-16 md:py-24 text-center overflow-hidden"
  style={{
    backgroundImage: `url(${banner2})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  }}
>
  {/*
  <h3 className="relative z-10 font-syne text-[28px] md:text-[40px] text-slate-900 tracking-tight font-medium">
    Mutual Funds
  </h3> */}

  <h3 className="relative z-10 font-syne text-[24px] md:text-[32px] text-slate-900 tracking-tight font-medium">
         Mutual Funds
        </h3>

  <div
    className="pointer-events-none absolute bottom-0 left-0 w-full h-[120px]"
    style={{
      background:
        "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.35) 40%, rgba(255,255,255,0.7) 70%, rgba(255,255,255,1) 100%)",
    }}
  />
</section>



      {/* ================= INTRO CONTENT SECTION ================= */}
      <section className="relative pt-20 pb-20 overflow-hidden bg-white">
        {/* Background Decor - Restored width for symmetry */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-slate-50 -z-10 hidden lg:block" />

        <div className="max-w-7xl mx-auto px-6 lg:px-24">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-15">
            
            {/* LEFT SIDE: CONTENT */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="w-full lg:w-3/5" // Takes up 60% of the space
            >
              
            
              <div className="space-y-6 max-w-xl"> {/* Prevents text from leaking into the center */}
                <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
                  Mutual Funds are investment vehicles that pool money from investors and allocate it across various asset classes such as equity, debt, and hybrid instruments. 
                </p>
                
                <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
                  These funds are managed by qualified professionals who follow a defined investment objective and strategy.
                </p>

                <p className="text-slate-500 text-base md:text-lg font-medium border-l-4 border-red-600 pl-6 py-2 mt-8 italic">
                  Mutual Funds offer a simple and structured way to participate in the financial markets with the benefit of diversification and disciplined investing.
                </p>
              </div>
            </motion.div>

            {/* RIGHT SIDE: IMAGE */}
            <motion.div 
              className="w-full lg:w-2/5 flex justify-end" // Takes up 40% and pins to the right margin
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
             <div className="relative w-full max-w-[480px] ml-5 mb-1">
  <img 
    src={mutualfunds}
    alt="Mutual Funds Overview"
    className="w-full h-auto object-contain"
  />
</div>

            </motion.div>

          </div>
        </div>
      </section>

<section 
  className="relative w-full pt-20 pb-12 overflow-hidden"
  style={{ 
    backgroundImage: `url(${kidBg})`, 
    backgroundSize: 'cover', 
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed'
  }}
>
  {/* TOP FADE: Aggressive white gradient to remove the top sharp edge */}
  <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-white via-white/50 to-transparent pointer-events-none z-0"></div>
  
  {/* BOTTOM FADE: Aggressive white gradient to remove the bottom sharp edge */}
  <div className="absolute bottom-0 left-0 w-full h-20 bg-gradient-to-t from-white via-white/50 to-transparent pointer-events-none z-0"></div>




  <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-24">
    
    {/* CENTERED HEADER */}
    <div className="text-center mb-12">
      <h3 className="text-[24px] md:text-[40px] font-[450] text-gray-900 tracking-tight mb-6">
        Why Invest in Mutual Funds?
      </h3>
      
      <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
        A mutual fund is a professionally managed investment vehicle that pools money from multiple investors 
        to invest in a diversified portfolio of stocks, bonds, or other securities. Investors in mutual funds 
        can earn returns in the following ways:
      </p>
    </div>

    {/* CENTRAL FOCUS LAYOUT */}
    <div className="relative min-h-[600px] flex items-center justify-center">
      
      {/* 1. THE CENTRAL IMAGE (The "Thinking Man") */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        className="relative z-20 w-full max-w-md "
      >
        <img 
          src={thinkingInvestor} 
          alt="Investor Thinking"
          className="w-full h-auto drop-shadow-2xl -translate-y-8 transform"
        />
      </motion.div>

      {/* 2. THE CALLOUT BUBBLES (Desktop Only) */}
     {/* 2. THE CALLOUT BUBBLES (Desktop Only) */}
{/* 2. THE CALLOUT BUBBLES (Desktop Only) */}
<div className="hidden lg:block">
  {benefits.map((item, index) => {
    const positions = [
      "top-[-15px] left-[35%] -translate-x-1/2", 
      "left-[-50px] top-[22%]",                  
      "right-[-50px] top-[22%]",                 
      "bottom-[95px] left-[0%]",                 
      "bottom-[95px] right-[0%]"                 
    ];

    return (
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        className={`absolute z-30 ${positions[index] || ""}`}
      >
        {/* Increased vertical padding (py-5) to give more height */}
<div className="bg-slate-800 text-white px-6 py-4 rounded-2xl shadow-xl w-[360px] relative border border-slate-700/50">

{index === 0 && (
          <div className="absolute w-4 h-4 bg-slate-800 rotate-45 -bottom-2 left-1/2 -translate-x-1/2"></div> )}

          {/* LEFT boxes → arrow RIGHT */}
{(index === 1 || index === 3) && (
  <div className="absolute top-1/2 -right-2 w-4 h-4 bg-slate-800 rotate-45 -translate-y-1/2"></div>
)}

{/* RIGHT boxes → arrow LEFT */}
{(index === 2 || index === 4) && (
  <div className="absolute top-1/2 -left-2 w-4 h-4 bg-slate-800 rotate-45 -translate-y-1/2"></div>
)}

          
          <div className="flex items-center gap-2 mb-2">
            <span className="text-red-400 scale-75 shrink-0">{item.icon}</span>
            <h4 className="text-[16px] font-bold tracking-tight leading-tight">{item.title}</h4>
          </div>

          {/* min-h-[64px] forces the bottom boxes to be the same height 
              as the top ones (roughly 4 lines of space) text-slate-600 leading-relaxed text-base md:text-md
          */}
          <p className="text-[14px] text-slate-300 leading-snug line-clamp-2">

            {item.desc}
          </p>
        </div>
      </motion.div>
    );
  })}
</div>
      {/* 3. MOBILE VIEW */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12 lg:hidden">
        {benefits.map((item, index) => (
          <div key={index} className="p-6 rounded-xl bg-white/90 border border-slate-100 shadow-lg">
             <div className="flex items-center gap-3 mb-2">
                <div className="text-red-600">{item.icon}</div>
                <h4 className="font-bold text-slate-800">{item.title}</h4>
             </div>
             <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </div>

    </div>
  </div>
</section>

  {/* ================= SECTION 3: WHO SHOULD INVEST (Side-by-Side) ================= */}

   <section className="pt-20 -pb-15 bg-white">
  <div className="max-w-7xl mx-auto px-6 lg:px-24">
    <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
      
      {/* LEFT SIDE: IMAGE */}
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="w-full lg:w-1/2" 
      >
        <div className="rounded-xl ">
          <img 
  src={investImg} 
  alt="Who Should Invest Illustration"
  className="w-full h-auto object-contain bg-white transform translate-y-2"
 />

 <p className="text-slate-500 text-base md:text-lg font-medium border-l-4 border-red-600 pl-6 py-2 mt-8 italic">
      Mutual Funds can suit a wide range of investors, depending on their financial objectives, investment horizon, and risk appetite
                </p>


        </div>
      </motion.div>

      {/* RIGHT SIDE: CONTENT */}
      <motion.div 
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="w-full lg:w-1/2"
      >
        <h3 className="text-[24px] md:text-[40px] font-[450] text-gray-900 tracking-tight mb-4 -mt-2">
          Who Should Invest?
        </h3>
        
        
        {/* Changed space-y-5 to space-y-2 for minimum vertical gap */}
        <div className="space-y-2">
          {targetAudience.map((text, idx) => (
            <div key={idx} className="flex items-start gap-2.5"> 
              {/* mt-[7px] refined for lg/xl text alignment */}
              <div className="flex-shrink-0 mt-[7px]">
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  className="text-slate-600" 
                  stroke="currentColor" 
                  strokeWidth="2.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                  <polyline points="9 11 12 14 22 4" />
                </svg>
              </div>
              
          {/*    <p className="text-slate-600 text-lg md:text-xl font-medium leading-snug"> */}
                 <p className="text-slate-600 text-base md:text-lg font-medium pl-1 ">
                {text}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

    </div>
  </div>
</section>
      {/* ================= SECTION 4: WHY THROUGH US ================= */}

 

<section className="pt-4 pb-24 bg-white overflow-hidden -mb-20">
  <div className="max-w-7xl mx-auto px-3 lg:px-10">
    <div className="flex flex-col lg:flex-row-reverse items-center gap-16 lg:gap-24">
      
      {/* RIGHT SIDE: IMAGE - No Cropping */}
     {/* LEFT SIDE: IMAGE */}
{/* LEFT SIDE: IMAGE */}
{/* RIGHT SIDE: IMAGE - Height Adjusted */}
{/* RIGHT SIDE: IMAGE */}
{/* RIGHT SIDE: IMAGE */}
<motion.div 
  initial={{ opacity: 0, x: 30 }}
  whileInView={{ opacity: 1, x: 0 }}
  viewport={{ once: true }}
  className="w-full lg:w-[55%] flex items-center justify-center" 
>
  <div className="w-full h-auto mt-4"> 
    <img 
      src={assistBg}
      alt="Investment Assistance Illustration"
      /* Pushed height to 720px for a more dominant look */
      className="w-full h-[800px] max-h-[600px] md:max-h-[650px] lg:max-h-[720px] object-contain block mx-auto"
    />
  </div>
</motion.div>

      {/* LEFT SIDE: STEPS CONTENT */}
      <motion.div 
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
       className="w-full lg:w-[45%]"

      >
        
         <h3 className="text-[24px] md:text-[40px] font-[450] text-gray-900 tracking-tight  mb-6">
                How We Assist
              </h3>
        
        {/* Changed space-y-12 to space-y-6 for tighter vertical distance */}
        <div className="relative space-y-6">
          {/* DARK GREY VERTICAL LINE */}
          <div className="absolute left-[19px] top-2 bottom-2 w-[2px] bg-slate-600 hidden md:block" />

          {[
            {
              title: "Structured Asset Allocation",
              desc: "We facilitate allocation across suitable fund categories based on your risk profile, financial goals, and investment horizon."
            },
            {
              title: "Disciplined Evaluation Framework",
              desc: "Investment options are assessed through a structured framework considering consistency, risk parameters, and suitability factors."
            },
            {
              title: "Seamless Execution",
              desc: "We coordinate documentation and transaction processes to ensure smooth and efficient investment execution."
            },
            {
              title: "Periodic Portfolio Reviews",
              desc: "We conduct periodic portfolio reviews to help ensure continued alignment with evolving financial objectives."
            }
          ].map((item, idx) => (
            <div key={idx} className="group relative flex gap-6 items-start transition-all duration-300">
              
              {/* CIRCLE: Matches Tick Color (slate-600) */}
              <div className="relative z-10 flex-shrink-0 w-10 h-10 rounded-full bg-white border-[2.5px] border-slate-600 flex items-center justify-center transition-all duration-300 group-hover:bg-red-700 group-hover:border-red-700">
                <span className="text-slate-600 font-bold text-sm transition-colors duration-300 group-hover:text-white">
                  {idx + 1}
                </span>
              </div>
              
              <div className="pt-1">
                <h4 className="text-slate-800 text-xl font-semibold tracking-tight mb-1 transition-colors duration-300 group-hover:text-red-700">
                  {item.title}
                </h4>
                <p className="text-slate-600 text-base md:text-lg  max-w-md font-medium">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

    </div>
  </div>
</section>
{/*
 <section
        className="py-10 md:py-14 text-center "
        style={{
          backgroundImage: `url(${faqBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        
    <h3 className="relative z-10 font-syne text-[28px] md:text-[40px] text-slate-900 tracking-tight font-medium">
   Why Through Us?
  </h3>
  
  <p className="mt-4 text-lg md:text-xl text-slate-600 leading-slug font-medium max-w-2xl mx-auto">
  Investing is made easy with our automated digital platform, while our expert fund selection, personalized advice, and active monitoring keep your portfolio smarter, safer, and goal-driven.
  </p>
      </section>
*/}
      <section
  className="py-10 md:py-14 text-center relative overflow-hidden "
  style={{
    backgroundImage: `url(${faqBg})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  }}
>
  {/* Shorter, more aggressive fade to keep the image original below the top edge */}
  <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-white via-white/20 to-transparent pointer-events-none z-0"></div>

  <div className="relative z-10 px-6">
    
     <h2 className="font-syne text-[24px] md:text-[32px] font-thin text-black/70 mb-2 md:mb-4 leading-[1.1] whitespace-pre-line tracking-tighter"> Why Through Us?</h2>

    <p className="mt-4 text-base md:text-lg text-slate-600  font-medium max-w-2xl mx-auto">
      Investing is made easy with our automated digital platform, while our
      expert fund selection, personalized advice, and active monitoring keep
      your portfolio smarter, safer, and goal-driven.
    </p>
  </div>
</section>
    </div>
  );
};

export default MutualFundsHero;

